import dayjs from 'dayjs';

import { DATE_KEY_FORMAT } from '@/helpers';

import { HORIZONTAL_WINDOW_SHIFT_DAYS, MAX_DATES_WINDOW } from '../../constants/sizes';
import { getPlannerDatesWindow } from '../../helpers/date';
import { appendDates, getInitialPlannerDates, prependDates } from '../helpers/dates-window';
import { logPlannerNavigation } from '../helpers/log';
import {
  buildDateReloadState,
  getNavigationTargetState,
  isStaleScrollDistance,
  recenterDatesWindow,
} from '../helpers/navigation-target';
import { DatesNavigationSlice, PlannerSliceCreator, PlannerStore } from '../types';
import { buildScrollCommand } from './scroll.slice';

/**
 * The horizontal dates window + all date navigation intents (arrows, today
 * button, date picker). Actions NEVER touch the DOM: a cache-hit navigation
 * queues a pendingScrollCommand (consumed by the scroll-command effect), a
 * cache-miss reload sets pendingScrollTarget (consumed by
 * use-planner-horizontal-lifecycle after the query settles).
 */

const formatRange = (range: { startDate: string; endDate: string } | null | undefined) =>
  `${range?.startDate}..${range?.endDate}`;

/**
 * Arrow navigation to the start of a month. Returns the state patch: always
 * records arrowNavTarget + queues a left-aligned scroll; regenerates the dates
 * window when the target is missing from it or too close to an edge.
 */
const navigateToDateStart = (
  state: PlannerStore,
  targetDate: dayjs.Dayjs,
  actionLabel: 'prevDateClick' | 'nextDateClick'
): Partial<PlannerStore> => {
  const { targetExists, shouldRegenerateDates } = getNavigationTargetState(state.dates, targetDate);
  const activeRange = state.queryDateRanges[state.groupBy];

  logPlannerNavigation(actionLabel, {
    firstVisible: (state.firstVisibleDate ?? dayjs()).format(DATE_KEY_FORMAT),
    target: targetDate.format(DATE_KEY_FORMAT),
    queryRange: formatRange(activeRange),
  });

  const navigationPatch = {
    arrowNavTarget: targetDate,
    pendingScrollCommand: buildScrollCommand(state.pendingScrollCommand, targetDate, 'start'),
  };

  if (targetExists && !shouldRegenerateDates) {
    logPlannerNavigation(actionLabel, { decision: 'target exists in dates array, just scrolling' });
    return navigationPatch;
  }

  logPlannerNavigation(actionLabel, {
    decision: targetExists
      ? 'target is too close to dates edge, regenerating dates'
      : 'target NOT in dates array, regenerating dates',
  });

  return {
    ...navigationPatch,
    ...recenterDatesWindow(targetDate, state.datesRegenerationTrigger),
  };
};

/**
 * Shared today-button / date-picker navigation: scroll immediately when the
 * target is rendered, loaded and trustworthy; otherwise regenerate the window
 * and reload data centered on the target (scroll happens after the query settles
 * via pendingScrollTarget).
 */
const navigateToDateCentered = (
  state: PlannerStore,
  targetDate: dayjs.Dayjs,
  actionLabel: 'setToday' | 'scrollToDate'
): Partial<PlannerStore> => {
  const dateStr = targetDate.format(DATE_KEY_FORMAT);
  const activeRange = state.queryDateRanges[state.groupBy];
  const { targetExists, shouldRegenerateDates } = getNavigationTargetState(state.dates, targetDate);

  logPlannerNavigation(actionLabel, {
    target: dateStr,
    queryRange: formatRange(activeRange),
    firstVisible: state.firstVisibleDate?.format(DATE_KEY_FORMAT),
  });

  // Check if the target date is within the currently loaded data range.
  // If so, we can scroll immediately without reloading data.
  const isInLoadedRange =
    activeRange && dateStr >= activeRange.startDate && dateStr <= activeRange.endDate;
  const isFarFromTarget = isStaleScrollDistance(state.firstVisibleDate, targetDate);

  if (targetExists && isInLoadedRange && !shouldRegenerateDates && !isFarFromTarget) {
    // Target is in DOM and data is loaded — scroll immediately
    logPlannerNavigation(actionLabel, { decision: 'in loaded range, scrolling immediately' });
    return {
      arrowNavTarget: null,
      pendingScrollCommand: buildScrollCommand(state.pendingScrollCommand, targetDate, 'center'),
    };
  }

  if (targetExists && isInLoadedRange && (shouldRegenerateDates || isFarFromTarget)) {
    logPlannerNavigation(actionLabel, {
      decision:
        'in loaded range but ' +
        (isFarFromTarget ? 'far from target' : 'too close to dates edge') +
        ', regenerating dates and reloading data',
    });
    return { arrowNavTarget: null, ...buildDateReloadState(state, targetDate) };
  }

  // Target is outside the loaded range — reload data, scroll after load.
  logPlannerNavigation(actionLabel, {
    decision: 'outside range',
    pendingScroll: dateStr,
    regenerateDates: shouldRegenerateDates,
  });
  return { arrowNavTarget: null, ...buildDateReloadState(state, targetDate) };
};

export const createDatesNavigationSlice: PlannerSliceCreator<DatesNavigationSlice> = set => ({
  dates: getInitialPlannerDates(),
  datesRegenerationTrigger: 0,
  arrowNavTarget: null,

  loadDatesLeft: () =>
    set(state => {
      // Prepend HORIZONTAL_WINDOW_SHIFT_DAYS days to the left then
      // always clamp to exactly MAX_DATES_WINDOW from the front.
      // Combined length is always > MAX_DATES_WINDOW so the trim always fires,
      // keeping scrollWidth perfectly stable.
      const combined = prependDates(state.dates, HORIZONTAL_WINDOW_SHIFT_DAYS);
      return { dates: combined.slice(0, MAX_DATES_WINDOW) };
    }),
  loadDatesRight: () =>
    set(state => {
      // Append HORIZONTAL_WINDOW_SHIFT_DAYS days to the right then
      // always clamp to exactly MAX_DATES_WINDOW from the tail.
      const combined = appendDates(state.dates, HORIZONTAL_WINDOW_SHIFT_DAYS);
      return { dates: combined.slice(combined.length - MAX_DATES_WINDOW) };
    }),
  expandDatesLeft: () =>
    set(state => ({ dates: prependDates(state.dates, HORIZONTAL_WINDOW_SHIFT_DAYS) })),
  expandDatesRight: () =>
    set(state => ({ dates: appendDates(state.dates, HORIZONTAL_WINDOW_SHIFT_DAYS) })),
  normalizeDatesWindow: (anchorDate: dayjs.Dayjs) =>
    set(() => ({ dates: getPlannerDatesWindow(anchorDate) })),

  prevDateClick: () =>
    set(state => {
      // Chain from the last arrow target (firstVisibleDate lags behind the
      // scroll animation) so consecutive clicks land on consecutive months.
      const currentDate = state.arrowNavTarget ?? state.firstVisibleDate ?? dayjs();
      const targetDate = currentDate.subtract(1, 'month').startOf('month');
      return navigateToDateStart(state, targetDate, 'prevDateClick');
    }),
  nextDateClick: () =>
    set(state => {
      const currentDate = state.arrowNavTarget ?? state.firstVisibleDate ?? dayjs();
      const targetDate = currentDate.add(1, 'month').startOf('month');
      return navigateToDateStart(state, targetDate, 'nextDateClick');
    }),

  setToday: () => set(state => navigateToDateCentered(state, dayjs(), 'setToday')),
  scrollToDate: (date: dayjs.Dayjs) =>
    set(state => navigateToDateCentered(state, date, 'scrollToDate')),
});
